// "Ready to merge": the open proposals that passed their targeted check and
// their review, and wait for their turn (step `queued`, reviewed `land`),
// with no landing of theirs running or waiting, held by a batch that stopped
// or not; and, beside them, those a person merges together while that
// batch runs or waits. While the project's policy has a person review
// (Poulpe-688), those that wait at their review for the person's verdict
// are there too. Two of them may conflict when their kept diffs change
// a file in common: a guess from paths, not a trial merge.
import type { Landing, Proposal, SessionChanges } from '../api/types'
import { mergeOf } from './merge'

export function isReady(proposal: Proposal, landings: ReadonlyMap<string, Landing>): boolean {
  if (proposal.state !== 'open' || proposal.step !== 'queued' || proposal.review?.verdict.outcome !== 'land') return false
  return ![...landings.values()].some((l) => l.proposals.includes(proposal.id) && l.state !== 'finished')
}

/** Whether, while a person reviews, it waits for their verdict: open at its review, with none yet. */
export function awaitsPerson(proposal: Proposal): boolean {
  return proposal.state === 'open' && proposal.step === 'review' && !proposal.review
}

/** A project's ready proposals, those in a batch, and, while a person reviews, those awaiting them; oldest first, as the queue takes them. */
export function readyProposals(project: string | null, proposals: ReadonlyMap<string, Proposal>, landings: ReadonlyMap<string, Landing>, personReviews = false): Proposal[] {
  return [...proposals.values()]
    .filter((p) => p.project_id === project && p.state === 'open' && (isReady(p, landings) || mergeOf(p.id, landings) !== null || (personReviews && awaitsPerson(p))))
    .sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : a.id < b.id ? -1 : 1))
}

/** Another ready proposal that changes some of the same files. */
export interface Overlap {
  with: Proposal
  paths: string[]
}

/** The paths a report changes, a rename's old one too. */
function pathsOf(changes: SessionChanges): Set<string> {
  return new Set(changes.files.flatMap((f) => (f.old_path ? [f.path, f.old_path] : [f.path])))
}

/**
 * For each ready proposal, the others whose kept diffs change a file it
 * changes too, in the order given, with those files sorted. One without a
 * copy yet overlaps nothing.
 */
export function mayConflict(ready: readonly Proposal[], copies: ReadonlyMap<string, SessionChanges | null | undefined>): Map<string, Overlap[]> {
  const paths = new Map(ready.flatMap((p) => {
    const changes = copies.get(p.id)
    return changes ? [[p.id, pathsOf(changes)] as const] : []
  }))
  const found = new Map<string, Overlap[]>()
  for (const one of ready) {
    const mine = paths.get(one.id)
    if (!mine) continue
    const overlaps = ready.flatMap((other) => {
      const theirs = other.id === one.id ? undefined : paths.get(other.id)
      const shared = theirs ? [...mine].filter((path) => theirs.has(path)).sort() : []
      return shared.length > 0 ? [{ with: other, paths: shared }] : []
    })
    if (overlaps.length > 0) found.set(one.id, overlaps)
  }
  return found
}
